import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, Button, Divider,
    Alert, Chip, LinearProgress, Tab, Tabs
} from '@mui/material';
import {
    ArrowBack, TrendingUp, TrendingDown, Visibility, Phone, Language
} from '@mui/icons-material';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Helper function to format KPI values
const formatKpiValue = (value) => {
    if (!value && value !== 0) return '0';
    const numValue = Number(value);
    if (isNaN(numValue)) return '0';
    if (numValue >= 1000000) return (numValue / 1000000).toFixed(1) + 'M';
    if (numValue >= 1000) return (numValue / 1000).toFixed(1) + 'K';
    return Math.round(numValue).toString();
};

// Helper function to calculate improvement percentage
const calculateImprovementPercentage = (beforeValue, afterValue) => {
    if (!beforeValue || beforeValue === 0) return afterValue > 0 ? 100 : 0;
    return Math.round(((afterValue - beforeValue) / beforeValue) * 100);
};

// Helper function to format date range
const formatDateRange = (timePeriod) => {
    if (!timePeriod || !timePeriod.start || !timePeriod.end) return 'N/A';

    const formatSingleDate = (date) => {
        if (!date || !date.day || !date.month || !date.year) return 'N/A';
        return `${String(date.day).padStart(2, '0')}.${String(date.month).padStart(2, '0')}.${date.year}`;
    };

    return `${formatSingleDate(timePeriod.start)} - ${formatSingleDate(timePeriod.end)}`;
};

const kpiOptions = [
    { key: 'impressions', label: 'Impressions', icon: <Visibility />, color: '#2196f3', metrics: ['BUSINESS_IMPRESSIONS_DESKTOP_MAPS', 'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH', 'BUSINESS_IMPRESSIONS_MOBILE_MAPS', 'BUSINESS_IMPRESSIONS_MOBILE_SEARCH'] },
    { key: 'CALL_CLICKS', label: 'Anrufe', icon: <Phone />, color: '#4caf50' },
    { key: 'WEBSITE_CLICKS', label: 'Website Klicks', icon: <Language />, color: '#9c27b0' },
];

const PerformanceDetailsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(0);

    // Get business data from location state
    const business = location.state?.business;

    // Extract metrics data - defined before any conditional returns
    const getMetricsForKpi = (kpiKey, period, biz) => {
        if (!biz || !biz[period] || !biz[period].summaryStats) return null;

        const stats = biz[period].summaryStats;
        const kpi = kpiOptions.find(k => k.key === kpiKey);

        if (kpiKey === 'impressions') {
            const impressionMetrics = kpi.metrics.map(metric => stats[metric] || { total: 0, average: 0 });
            const total = impressionMetrics.reduce((sum, m) => sum + (m.total || 0), 0);
            const average = impressionMetrics.reduce((sum, m) => sum + (m.average || 0), 0);
            return { total, average, breakdown: impressionMetrics };
        } else {
            return stats[kpiKey] || { total: 0, average: 0, max: 0, min: 0 };
        }
    };

    // Calculate improvements for all KPIs - moved before conditional
    const improvements = useMemo(() => {
        if (!business) return [];
        return kpiOptions.map(kpi => {
            const beforeMetrics = getMetricsForKpi(kpi.key, 'beforePeriodData', business);
            const afterMetrics = getMetricsForKpi(kpi.key, 'afterPeriodData', business);

            return {
                key: kpi.key,
                label: kpi.label,
                color: kpi.color,
                icon: kpi.icon,
                improvement: calculateImprovementPercentage(beforeMetrics?.total || 0, afterMetrics?.total || 0),
                before: beforeMetrics,
                after: afterMetrics
            };
        });
    }, [business]);

    // Generate time series data for charts using actual data
    const timeSeriesData = useMemo(() => {
        if (!business) return [];

        // Create array of data points for the line chart
        const dataPoints = [];

        // Get time series data from before and after periods
        // First try to get from extracted timeSeries format
        let beforeTimeSeries = business.beforePeriodData?.timeSeries;
        let afterTimeSeries = business.afterPeriodData?.timeSeries;

        // Extract from raw API response if needed
        const extractTimeSeriesFromRaw = (rawResponse) => {
            if (!rawResponse?.multiDailyMetricTimeSeries) return null;

            const timeSeries = {
                BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: [],
                BUSINESS_IMPRESSIONS_MOBILE_SEARCH: [],
                BUSINESS_IMPRESSIONS_DESKTOP_MAPS: [],
                BUSINESS_IMPRESSIONS_MOBILE_MAPS: [],
                CALL_CLICKS: [],
                WEBSITE_CLICKS: []
            };

            rawResponse.multiDailyMetricTimeSeries.forEach(metricGroup => {
                if (!metricGroup.dailyMetricTimeSeries) return;

                metricGroup.dailyMetricTimeSeries.forEach(dailyMetric => {
                    const metricType = dailyMetric.dailyMetric;
                    if (dailyMetric.timeSeries?.datedValues) {
                        // Extract ALL daily values including zeros
                        timeSeries[metricType] = dailyMetric.timeSeries.datedValues.map(dv =>
                            parseInt(dv.value) || 0
                        );
                    }
                });
            });

            return timeSeries;
        };

        // If we don't have extracted timeSeries, try to extract from raw
        if (!beforeTimeSeries && business.beforePeriodData?.raw) {
            beforeTimeSeries = extractTimeSeriesFromRaw(business.beforePeriodData.raw);
        }
        if (!afterTimeSeries && business.afterPeriodData?.raw) {
            afterTimeSeries = extractTimeSeriesFromRaw(business.afterPeriodData.raw);
        }

        // If we only have one period (fresh data), use what we have
        if (!beforeTimeSeries && !afterTimeSeries) return [];

        // Use available data - if fresh data was fetched, both will have same data
        const hasBeforeData = !!beforeTimeSeries;
        const hasAfterData = !!afterTimeSeries;
        const timeSeriesToUse = beforeTimeSeries || afterTimeSeries;

        // Get the maximum length from any metric array
        const length = timeSeriesToUse.BUSINESS_IMPRESSIONS_DESKTOP_MAPS?.length || 0;

        // Helper function to get value from metric array
        const getMetricValue = (metricArray, index) => {
            if (!metricArray || index >= metricArray.length) return 0;
            const dataPoint = metricArray[index];

            // Handle direct number format (from extracted timeSeries)
            if (typeof dataPoint === 'number') {
                return Math.max(0, dataPoint); // Ensure non-negative
            }

            // Handle object formats { value: X } and { data: X }
            if (dataPoint && typeof dataPoint === 'object') {
                if (dataPoint.value !== undefined) return Math.max(0, parseInt(dataPoint.value) || 0);
                if (dataPoint.data !== undefined) return Math.max(0, parseInt(dataPoint.data) || 0);
            }

            return 0;
        };

        // Helper function to calculate date for each data point
        const calculateDateForIndex = (startDate, index) => {
            if (!startDate) return 'Day ' + (index + 1);

            // Handle different date formats
            let dateObj;

            // Format: { day, month, year }
            if (startDate.day && startDate.month && startDate.year) {
                dateObj = new Date(startDate.year, startDate.month - 1, startDate.day);
            }
            // Format: ISO string or direct date
            else if (typeof startDate === 'string') {
                dateObj = new Date(startDate);
            }
            // Format: Date object
            else if (startDate instanceof Date) {
                dateObj = new Date(startDate);
            }
            else {
                return 'Day ' + (index + 1);
            }

            // Add index days
            dateObj.setDate(dateObj.getDate() + index);

            const day = dateObj.getDate();
            const month = dateObj.getMonth() + 1;
            const year = dateObj.getFullYear();

            return `${day}.${month}.${year}`;
        };

        // Get start dates for both periods - check multiple possible paths
        let beforeStartDate = business.beforePeriodData?.timePeriod?.start;
        let afterStartDate = business.afterPeriodData?.timePeriod?.start;

        // Fallback if structure is different
        if (!beforeStartDate) {
            beforeStartDate = business.beforePeriod?.start;
        }
        if (!afterStartDate) {
            afterStartDate = business.afterPeriod?.start;
        }

        // Extract dates from raw API response if available
        let datesFromRaw = null;
        if (business.beforePeriodData?.raw?.multiDailyMetricTimeSeries?.[0]?.dailyMetricTimeSeries?.[0]?.timeSeries?.datedValues) {
            datesFromRaw = business.beforePeriodData.raw.multiDailyMetricTimeSeries[0].dailyMetricTimeSeries[0].timeSeries.datedValues.map(dv => dv.date);
        } else if (business.afterPeriodData?.raw?.multiDailyMetricTimeSeries?.[0]?.dailyMetricTimeSeries?.[0]?.timeSeries?.datedValues) {
            datesFromRaw = business.afterPeriodData.raw.multiDailyMetricTimeSeries[0].dailyMetricTimeSeries[0].timeSeries.datedValues.map(dv => dv.date);
        }

        // Create data points by combining before and after time series
        for (let i = 0; i < length; i++) {
            // Determine date - prefer raw dates, then calculate from start date
            let date;
            if (datesFromRaw && datesFromRaw[i]) {
                const d = datesFromRaw[i];
                date = `${d.day}.${d.month}.${d.year}`;
            } else {
                date = beforeStartDate ? calculateDateForIndex(beforeStartDate, i) : calculateDateForIndex(afterStartDate, i);
            }

            const point = {
                day: i + 1,
                date: date, // Add formatted date
                // Impressions: sum of all impression types
                impressions_before: hasBeforeData ? (
                    getMetricValue(beforeTimeSeries.BUSINESS_IMPRESSIONS_DESKTOP_MAPS, i) +
                    getMetricValue(beforeTimeSeries.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH, i) +
                    getMetricValue(beforeTimeSeries.BUSINESS_IMPRESSIONS_MOBILE_MAPS, i) +
                    getMetricValue(beforeTimeSeries.BUSINESS_IMPRESSIONS_MOBILE_SEARCH, i)
                ) : 0,
                impressions_after: hasAfterData ? (
                    getMetricValue(afterTimeSeries.BUSINESS_IMPRESSIONS_DESKTOP_MAPS, i) +
                    getMetricValue(afterTimeSeries.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH, i) +
                    getMetricValue(afterTimeSeries.BUSINESS_IMPRESSIONS_MOBILE_MAPS, i) +
                    getMetricValue(afterTimeSeries.BUSINESS_IMPRESSIONS_MOBILE_SEARCH, i)
                ) : 0,
                // Call Clicks
                calls_before: hasBeforeData ? getMetricValue(beforeTimeSeries.CALL_CLICKS, i) : 0,
                calls_after: hasAfterData ? getMetricValue(afterTimeSeries.CALL_CLICKS, i) : 0,
                // Website Clicks
                website_before: hasBeforeData ? getMetricValue(beforeTimeSeries.WEBSITE_CLICKS, i) : 0,
                website_after: hasAfterData ? getMetricValue(afterTimeSeries.WEBSITE_CLICKS, i) : 0
            };
            dataPoints.push(point);
        }

        return dataPoints;
    }, [business]);

    const handleTabChange = (event, _newValue) => {
        setActiveTab(_newValue);
    };

    // Now check if business exists after all hooks are called
    if (!business) {
        return (
            <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/top-performers')}>
                    Zurück
                </Button>
                <Alert severity="error" sx={{ mt: 2 }}>
                    Keine Geschäftsdaten vorhanden. Bitte wählen Sie ein Unternehmen aus der Top-Performer-Liste.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header with Back Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<ArrowBack />} onClick={() => navigate('/top-performers')} variant="outlined">
                        Zurück
                    </Button>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        Performance Detail
                    </Typography>

                    <Typography variant="caption" sx={{ fontWeight: 600, color:'red' }}>
                        {business.businessName || 'Unbekanntes Unternehmen'}
                    </Typography>
                </Box>
            </Box>

           

            {/* KPI Overview Cards */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>KPI-Verbesserungen</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {improvements.map((kpi) => (
                    <Grid item xs={12} md={4} key={kpi.key}>
                        <Card sx={{
                            height: '100%',
                            border: `2px solid ${kpi.improvement > 0 ? '#4caf50' : kpi.improvement < 0 ? '#f44336' : '#e0e0e0'}`,
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">{kpi.label}</Typography>
                                    {kpi.improvement > 0 ? (
                                        <TrendingUp sx={{ color: '#4caf50', fontSize: 28 }} />
                                    ) : kpi.improvement < 0 ? (
                                        <TrendingDown sx={{ color: '#f44336', fontSize: 28 }} />
                                    ) : null}
                                </Box>

                                <Typography variant="h4" sx={{
                                    color: kpi.improvement > 0 ? '#4caf50' : '#f44336',
                                    mb: 2,
                                    fontWeight: 'bold'
                                }}>
                                    {kpi.improvement > 0 ? '+' : ''}{kpi.improvement}%
                                </Typography>

                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Vorher:</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {formatKpiValue(kpi.before?.total || 0)}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary">Nachher:</Typography>
                                    <Typography variant="body2" fontWeight="bold" sx={{ color: kpi.color }}>
                                        {formatKpiValue(kpi.after?.total || 0)}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Tabs for Detailed View */}
            <Card>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    <Tab label="Vorher-Zeitreihen" sx={{ color: '#f44336', fontWeight: activeTab === 0 ? 'bold' : 'normal' }} />
                    <Tab label="Nachher-Zeitreihen" sx={{ color: '#4caf50', fontWeight: activeTab === 1 ? 'bold' : 'normal' }} />
                    <Tab label="Detaillierte Metriken" sx={{ color: '#7d4cafff', fontWeight: activeTab === 1 ? 'bold' : 'normal' }} />
                    <Tab label="Zeitraum-Vergleich" sx={{ color: '#01bf9cff', fontWeight: activeTab === 1 ? 'bold' : 'normal' }} />
                    <Tab label="Statistiken" sx={{ color: '#f98703ff', fontWeight: activeTab === 1 ? 'bold' : 'normal' }} />
                </Tabs>

                <CardContent>
                    {/* Tab 0: Vorher-Zeitreihen (Before Period Charts) */}
                    {activeTab === 0 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 3, color: '#f44336', fontWeight: 600 }}>
                                📍 Vorher-Zeitraum ({formatDateRange(business.beforePeriod)})
                            </Typography>

                            {/* Impressions Before Chart */}
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Impressions (Vorher)
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={timeSeriesData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={Math.floor(timeSeriesData.length / 6)} />
                                            <YAxis />
                                            <Tooltip formatter={(value) => formatKpiValue(value)} labelFormatter={(label) => `Date: ${label}`} />
                                            <Line type="monotone" dataKey="impressions_before" stroke="#f44336" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>

                            {/* Call Clicks Before Chart */}
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Anrufe (Vorher)
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={timeSeriesData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={Math.floor(timeSeriesData.length / 6)} />
                                            <YAxis />
                                            <Tooltip formatter={(value) => formatKpiValue(value)} labelFormatter={(label) => `Date: ${label}`} />
                                            <Bar dataKey="calls_before" fill="#f44336" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>

                            {/* Website Clicks Before Chart */}
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Website Klicks (Vorher)
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={timeSeriesData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={Math.floor(timeSeriesData.length / 6)} />
                                            <YAxis />
                                            <Tooltip formatter={(value) => formatKpiValue(value)} labelFormatter={(label) => `Date: ${label}`} />
                                            <Line type="monotone" dataKey="website_before" stroke="#f44336" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {/* Tab 1: Nachher-Zeitreihen (After Period Charts) */}
                    {activeTab === 1 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 3, color: '#4caf50', fontWeight: 600 }}>
                                📍 Nachher-Zeitraum ({formatDateRange(business.afterPeriod)})
                            </Typography>

                            {/* Impressions After Chart */}
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Impressions (Nachher)
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={timeSeriesData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={Math.floor(timeSeriesData.length / 6)} />
                                            <YAxis />
                                            <Tooltip formatter={(value) => formatKpiValue(value)} labelFormatter={(label) => `Date: ${label}`} />
                                            <Line type="monotone" dataKey="impressions_after" stroke="#4caf50" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>

                            {/* Call Clicks After Chart */}
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Anrufe (Nachher)
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={timeSeriesData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={Math.floor(timeSeriesData.length / 6)} />
                                            <YAxis />
                                            <Tooltip formatter={(value) => formatKpiValue(value)} labelFormatter={(label) => `Date: ${label}`} />
                                            <Bar dataKey="calls_after" fill="#4caf50" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>

                            {/* Website Clicks After Chart */}
                            <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Website Klicks (Nachher)
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={timeSeriesData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} interval={Math.floor(timeSeriesData.length / 6)} />
                                            <YAxis />
                                            <Tooltip formatter={(value) => formatKpiValue(value)} labelFormatter={(label) => `Date: ${label}`} />
                                            <Line type="monotone" dataKey="website_after" stroke="#4caf50" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {/* Tab 2: Detailed Metrics */}
                    {activeTab === 2 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2 }}>Detaillierte Metriken nach KPI</Typography>
                            {improvements.map((kpi) => (
                                <Box key={kpi.key} sx={{ mb: 3 }}>
                                    <Divider sx={{ mb: 2 }} />
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: kpi.color }}>
                                        {kpi.label}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Vorher ({formatDateRange(business.beforePeriod)})</Typography>
                                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                                <Typography variant="body2"><strong>Gesamt:</strong> {formatKpiValue(kpi.before?.total || 0)}</Typography>
                                                <Typography variant="body2"><strong>Durchschnitt:</strong> {(kpi.before?.average || 0).toFixed(2)}</Typography>
                                                {kpi.before?.max !== undefined && (
                                                    <>
                                                        <Typography variant="body2"><strong>Max:</strong> {kpi.before?.max}</Typography>
                                                        <Typography variant="body2"><strong>Min:</strong> {kpi.before?.min}</Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Nachher ({formatDateRange(business.afterPeriod)})</Typography>
                                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                                <Typography variant="body2"><strong>Gesamt:</strong> {formatKpiValue(kpi.after?.total || 0)}</Typography>
                                                <Typography variant="body2"><strong>Durchschnitt:</strong> {(kpi.after?.average || 0).toFixed(2)}</Typography>
                                                {kpi.after?.max !== undefined && (
                                                    <>
                                                        <Typography variant="body2"><strong>Max:</strong> {kpi.after?.max}</Typography>
                                                        <Typography variant="body2"><strong>Min:</strong> {kpi.after?.min}</Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 2, p: 2, bgcolor: kpi.improvement > 0 ? '#e8f5e9' : '#ffebee', borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ color: kpi.improvement > 0 ? '#2e7d32' : '#c62828', fontWeight: 'bold' }}>
                                            Verbesserung: {kpi.improvement > 0 ? '+' : ''}{kpi.improvement}%
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Tab 3: Period Comparison */}
                    {activeTab === 3 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2 }}>Zeitraum-Vergleich</Typography>

                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#f44336' }}>
                                                Vorher-Zeitraum
                                            </Typography>
                                            <Typography variant="body2">{formatDateRange(business.beforePeriod)}</Typography>
                                            {business.beforePeriod && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                        Von: {business.beforePeriod.start?.day}.{business.beforePeriod.start?.month}.{business.beforePeriod.start?.year}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        Bis: {business.beforePeriod.end?.day}.{business.beforePeriod.end?.month}.{business.beforePeriod.end?.year}
                                                    </Typography>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#4caf50' }}>
                                                Nachher-Zeitraum
                                            </Typography>
                                            <Typography variant="body2">{formatDateRange(business.afterPeriod)}</Typography>
                                            {business.afterPeriod && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                        Von: {business.afterPeriod.start?.day}.{business.afterPeriod.start?.month}.{business.afterPeriod.start?.year}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        Bis: {business.afterPeriod.end?.day}.{business.afterPeriod.end?.month}.{business.afterPeriod.end?.year}
                                                    </Typography>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            <Alert severity="info">
                                Die Daten wurden über diese beiden Zeiträume verglichen, um die Verbesserungen zu berechnen.
                            </Alert>
                        </Box>
                    )}

                    {/* Tab 4: Statistics */}
                    {activeTab === 4 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2 }}>Statistiken und Übersicht</Typography>

                            <Grid container spacing={2}>
                                {improvements.map((kpi) => (
                                    <Grid item xs={12} md={6} key={`stats-${kpi.key}`}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                                    {kpi.label}
                                                </Typography>

                                                <Box sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                        <Typography variant="caption">Verbesserung</Typography>
                                                        <Chip
                                                            label={`${kpi.improvement > 0 ? '+' : ''}${kpi.improvement}%`}
                                                            color={kpi.improvement > 0 ? 'success' : kpi.improvement < 0 ? 'error' : 'default'}
                                                            size="small"
                                                        />
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(Math.max((kpi.improvement + 100) / 2, 0), 100)}
                                                        sx={{ height: 8, borderRadius: 4 }}
                                                    />
                                                </Box>

                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                    <strong>Vorher:</strong> {formatKpiValue(kpi.before?.total || 0)} (Ø {(kpi.before?.average || 0).toFixed(1)})
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    <strong>Nachher:</strong> {formatKpiValue(kpi.after?.total || 0)} (Ø {(kpi.after?.average || 0).toFixed(1)})
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default PerformanceDetailsPage;
